import type { OnDestroy, Signal, WritableSignal } from '@angular/core';
import { computed, inject, Injectable, signal } from '@angular/core';

import { AuthenticationService } from '#/app/shared/services/auth/authentication.service';
import { AuthSessionRefresherService } from '#/app/shared/services/auth-session-refresher.service';
import { ConfigurationService } from '#/app/shared/services/configuration.service';

import { SessionSyncService } from './session-sync.service';
import {
  SESSION_IDLE_MS_DEFAULT,
  SESSION_WARNING_COUNTDOWN_MS,
} from './session-timeout.config';

export interface ISessionTimeoutService {
  /**
   * has the user been inactive for long enough to show the warning?
   * @see https://dev.azure.com/KHTechnologySolutions/Decision%20Support%20Tool/_workitems/edit/5406/
   */
  isWarningVisible: Signal<boolean>;

  /**
   * remaining seconds until session timeout
   * @see https://dev.azure.com/KHTechnologySolutions/Decision%20Support%20Tool/_workitems/edit/5406/
   */
  secondsRemaining: Signal<number>;
}

/**
 * Manages session inactivity, warning, and expiry signals.
 * @see https://dev.azure.com/KHTechnologySolutions/Decision%20Support%20Tool/_workitems/edit/5406/
 */
@Injectable({ providedIn: 'root' })
export class SessionTimeoutService
  implements ISessionTimeoutService, OnDestroy
{
  readonly #authenticationService = inject(AuthenticationService);
  readonly #SessionSyncService = inject(SessionSyncService);
  readonly #sessionRefresherService = inject(AuthSessionRefresherService);
  readonly #configurationService = inject(ConfigurationService);

  readonly #abortController = new AbortController();

  readonly #warningStartMs: WritableSignal<number | null> = signal<
    number | null
  >(null);
  readonly #nowMs: WritableSignal<number> = signal<number>(Date.now());
  readonly #lastActivityMs: WritableSignal<number> = signal(Date.now());

  readonly #timeTickInterval = setInterval(() => {
    this.#handleTimeTick();
  }, 1000);

  public readonly isWarningVisible: Signal<boolean> = computed(() => {
    return this.#warningStartMs() !== null && !this.isExpired();
  });

  readonly secondsRemaining: Signal<number> = computed(() => {
    const timeSinceLastActivity = this.#nowMs() - this.#lastActivityMs();
    const remainingMs = this.resolveSessionIdleMs() - timeSinceLastActivity;
    return Math.ceil(remainingMs / 1000);
  });

  /**
   * public readonly lastActivityMs
   * @see https://dev.azure.com/KHTechnologySolutions/Decision%20Support%20Tool/_workitems/edit/5406/
   */
  readonly lastActivityMs: Signal<number> = computed(() => {
    return this.#lastActivityMs();
  });

  /**
   * has the session has expired due to inactivity?
   * @see https://dev.azure.com/KHTechnologySolutions/Decision%20Support%20Tool/_workitems/edit/5406/
   */
  public readonly isExpired: Signal<boolean> = computed(() => {
    return this.secondsRemaining() <= 0;
  });

  constructor() {
    this.#addActivityListeners();
  }

  ngOnDestroy(): void {
    this.#abortController.abort();
    clearInterval(this.#timeTickInterval);
  }

  continueSession(): void {
    void this.#sessionRefresherService.maybeRefreshOnContinue().then(() => {
      this.#startTimeoutTimerAtTime(Date.now());
    });
  }

  /**
   * Max amount of time (in milliseconds) a session can be idle before timing out.
   * Uses environment config if available and clamps to reasonable bounds
   * otherwise falls back to default
   * @returns {number}
   * @see https://dev.azure.com/KHTechnologySolutions/Decision%20Support%20Tool/_workitems/edit/5406/
   */
  resolveSessionIdleMs(): number {
    const configIdleSeconds = Number(
      this.#configurationService.configuration.sessionIdleSecondsBeforeTimeout,
    );
    if (!configIdleSeconds) return SESSION_IDLE_MS_DEFAULT;

    const maxSeconds = 60 * 60;
    const minSeconds = 5 * 60;
    const idleSecondsClamped = Math.max(
      minSeconds,
      Math.min(maxSeconds, configIdleSeconds),
    );
    return idleSecondsClamped * 1000;
  }

  broadcastActivityToOtherTabs(): void {
    this.#SessionSyncService.broadcastMessage({
      type: 'ACTIVITY',
      timestamp: Date.now(),
    });
  }

  #startTimeoutTimerAtTime(sessionStartMs: number) {
    this.#lastActivityMs.set(sessionStartMs);
    this.#warningStartMs.set(null);
  }

  #handleTimeTick() {
    if (this.isExpired()) return;

    this.#nowMs.set(Date.now());

    this.#checkForAndHandleActivityFromOtherTabs();

    this.#updateWarningStartMs();

    if (this.isExpired() && this.#authenticationService.isAuthenticated())
      this.#authenticationService.logout();
  }

  #updateWarningStartMs(): void {
    const timeSinceActivityMs = this.#nowMs() - this.#lastActivityMs();
    const shouldWarn =
      timeSinceActivityMs >=
      this.resolveSessionIdleMs() - SESSION_WARNING_COUNTDOWN_MS;
    if (shouldWarn && this.#warningStartMs() === null) {
      this.#warningStartMs.set(this.#nowMs());
    } else if (!shouldWarn) {
      this.#warningStartMs.set(null);
    }
  }

  #checkForAndHandleActivityFromOtherTabs(): void {
    const incomingActivityMs = this.#SessionSyncService.lastActivityMs();
    if (incomingActivityMs > this.#lastActivityMs()) {
      this.#handleActivity(incomingActivityMs);
    }
  }

  #handleLocalActivity(): void {
    if (document.visibilityState !== 'visible') return;
    if (this.isWarningVisible()) return;

    const lastActivityDebounceMs = 1000;
    const elapsedMsSinceLastActivity = Date.now() - this.#lastActivityMs();
    if (elapsedMsSinceLastActivity < lastActivityDebounceMs) return;

    this.#handleActivity(Date.now());

    this.broadcastActivityToOtherTabs();
  }

  #handleActivity(activityMs: number): void {
    if (this.isExpired()) return;
    this.#startTimeoutTimerAtTime(activityMs);
  }

  #addActivityListeners(): void {
    ['click', 'keydown', 'pointerdown', 'scroll', 'visibilitychange'].forEach(
      (event) => {
        window.addEventListener(
          event,
          () => {
            this.#handleLocalActivity();
          },
          { signal: this.#abortController.signal },
        );
      },
    );
  }
}
